import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { errMsg, LOGO_URL } from "@/lib/api";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const nav = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(username, password);
      nav("/admin", { replace: true });
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#08080a] px-5 grain">
      <div className="grid-floor absolute inset-0 opacity-50" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(225,29,72,0.16),transparent_60%)]" />

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="metal-panel relative w-full max-w-md p-8 sm:p-10"
      >
        <img src={LOGO_URL} alt="TURBOLUX" className="h-10 w-auto object-contain" />
        <h1 className="mt-7 font-head text-3xl font-extrabold uppercase italic tracking-tight">
          <span className="metal-text">ADMIN</span> <span className="crimson-text">PANELIS</span>
        </h1>
        <p className="mt-2 font-mono-t text-[10px] uppercase tracking-[0.25em] text-slate-500">
          Pieeja tikai servisa komandai
        </p>

        <form onSubmit={submit} className="mt-8 space-y-4" data-testid="admin-login-form">
          <label className="block">
            <span className="font-mono-t text-[10px] uppercase tracking-[0.2em] text-slate-500">Lietotājvārds</span>
            <input
              type="text"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              data-testid="admin-login-username"
              className="mt-2 w-full border border-white/10 bg-black/50 px-4 py-3 text-sm text-slate-100 outline-none focus:border-rose-500"
              required
            />
          </label>
          <label className="block">
            <span className="font-mono-t text-[10px] uppercase tracking-[0.2em] text-slate-500">Parole</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-login-password"
              className="mt-2 w-full border border-white/10 bg-black/50 px-4 py-3 text-sm text-slate-100 outline-none focus:border-rose-500"
              required
            />
          </label>

          {error && (
            <div data-testid="admin-login-error" className="border border-rose-600/40 bg-rose-600/10 px-4 py-3 text-xs text-rose-300">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="flex w-full items-center justify-center gap-3 bg-rose-600 py-3.5 font-head text-lg font-bold uppercase tracking-wide text-white transition-colors hover:bg-rose-500 disabled:opacity-60"
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            Ieiet
          </button>
        </form>

        <Link
          to="/"
          data-testid="admin-login-back"
          className="mt-7 inline-flex items-center gap-2 font-mono-t text-[10px] uppercase tracking-[0.2em] text-slate-500 hover:text-slate-300"
        >
          <ArrowLeft className="h-3 w-3" /> Atpakaļ uz mājaslapu
        </Link>
      </motion.div>
    </div>
  );
}
